/**
 * AvailabilityModal Component
 *
 * Modal for barbers to set their availability status with reasons.
 * Supports:
 * - Eating (temporal - 30-60 min)
 * - Quick errand (temporal - custom minutes)
 * - Personal matters (temporal - custom minutes)
 * - Day off (not available for the rest of the day)
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { toast } from '@/lib/toast';
import {
  X,
  Coffee,
  Car,
  User,
  Calendar,
  Clock,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import type { BreakReason } from '@/types';

interface AvailabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  barberId?: string;
  currentStatus?: 'available' | 'on_break' | 'offline';
  currentReason?: BreakReason;
  currentReturnTime?: Date | null;
  onStatusChanged?: () => void;
}

const BREAK_REASONS: {
  id: BreakReason;
  label: string;
  icon: typeof Coffee;
  defaultMinutes: number;
  isTemporary: boolean;
  description: string;
}[] = [
  {
    id: 'eating',
    label: 'Comiendo',
    icon: Coffee,
    defaultMinutes: 45,
    isTemporary: true,
    description: 'Pausa para comer. Los clientes pueden reservar después de tu regreso.',
  },
  {
    id: 'quick_errand',
    label: 'Gestión rápida',
    icon: Car,
    defaultMinutes: 15,
    isTemporary: true,
    description: 'Vuelta rápida. Indica cuántos minutos tardarás.',
  },
  {
    id: 'personal',
    label: 'Asuntos personales',
    icon: User,
    defaultMinutes: 30,
    isTemporary: true,
    description: 'Los clientes pueden reservar después de tu regreso.',
  },
  {
    id: 'day_off',
    label: 'Hoy libro',
    icon: Calendar,
    defaultMinutes: 0,
    isTemporary: false,
    description: 'No disponible por el resto del día.',
  },
];

export function AvailabilityModal({
  isOpen,
  onClose,
  barberId,
  currentStatus = 'available',
  currentReason,
  currentReturnTime,
  onStatusChanged,
}: AvailabilityModalProps) {
  const [selectedReason, setSelectedReason] = useState<BreakReason | null>(currentReason || null);
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const selectedConfig = BREAK_REASONS.find((r) => r.id === selectedReason);

  const handleSetUnavailable = async () => {
    if (!selectedReason) {
      toast.error('Selecciona un motivo');
      return;
    }

    setLoading(true);
    try {
      const setBarberAvailability = httpsCallable(functions, 'setBarberAvailability');
      await setBarberAvailability({
        barberId,
        status: selectedReason === 'day_off' ? 'offline' : 'on_break',
        reason: selectedReason,
        durationMinutes: selectedConfig?.isTemporary ? durationMinutes : undefined,
      });

      toast.success(
        selectedReason === 'day_off'
          ? 'Estado actualizado: No disponible hoy'
          : `Estado actualizado: ${selectedConfig?.label} (${durationMinutes} min)`
      );

      onStatusChanged?.();
      onClose();
    } catch (error) {
      console.error('Error setting availability:', error);
      toast.error('Error al actualizar disponibilidad');
    } finally {
      setLoading(false);
    }
  };

  const handleSetAvailable = async () => {
    setLoading(true);
    try {
      const setBarberAvailability = httpsCallable(functions, 'setBarberAvailability');
      await setBarberAvailability({
        barberId,
        status: 'available',
      });

      toast.success('Estado actualizado: Disponible');
      onStatusChanged?.();
      onClose();
    } catch (error) {
      console.error('Error setting availability:', error);
      toast.error('Error al actualizar disponibilidad');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-surface border border-hairline rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-hairline">
          <h2 className="font-sans text-lg font-semibold text-ink">
            {currentStatus === 'available'
              ? 'Marcar no disponible'
              : 'Estado de disponibilidad'
            }
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-ink-subtle hover:text-ink hover:bg-surface-2 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Current status display */}
          {currentStatus !== 'available' && (
            <div className="mb-6 p-4 bg-warning/10 rounded-lg border border-warning/30">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-warning/15 rounded-full">
                  <Clock className="w-5 h-5 text-warning" aria-hidden="true" />
                </div>
                <div>
                  <p className="font-semibold text-ink">
                    Estado actual: {BREAK_REASONS.find(r => r.id === currentReason)?.label || 'No disponible'}
                  </p>
                  {currentReturnTime && (
                    <p className="text-sm text-ink-muted tabular-nums">
                      Regreso estimado: {currentReturnTime.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              </div>

              <button
                onClick={handleSetAvailable}
                disabled={loading}
                className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent text-on-accent hover:bg-accent-hover disabled:opacity-50 font-bold rounded-lg transition-colors"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle size={20} />
                    Volver a estar disponible
                  </>
                )}
              </button>
            </div>
          )}

          {/* Reason selection */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-ink-muted">
              {currentStatus === 'available' ? 'Selecciona el motivo:' : 'Cambiar motivo:'}
            </p>

            {BREAK_REASONS.map((reason) => {
              const Icon = reason.icon;
              const isSelected = selectedReason === reason.id;

              return (
                <button
                  key={reason.id}
                  onClick={() => {
                    setSelectedReason(reason.id);
                    setDurationMinutes(reason.defaultMinutes);
                  }}
                  className={`w-full flex items-start gap-3 p-4 rounded-lg border transition-colors ${
                    isSelected
                      ? 'border-accent bg-accent/10'
                      : 'border-hairline bg-surface-2 hover:border-hairline-strong'
                  }`}
                >
                  <div className={`p-2 rounded-full ${
                    isSelected ? 'bg-accent/15' : 'bg-surface-3'
                  }`}>
                    <Icon className={`w-5 h-5 ${
                      isSelected ? 'text-accent' : 'text-ink-subtle'
                    }`} aria-hidden="true" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className={`font-semibold ${
                      isSelected ? 'text-accent' : 'text-ink'
                    }`}>
                      {reason.label}
                    </p>
                    <p className="text-sm text-ink-muted">
                      {reason.description}
                    </p>
                  </div>
                  {reason.isTemporary && (
                    <span className="px-2 py-1 text-xs font-medium bg-surface-3 text-ink-muted rounded">
                      Temporal
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Duration selector for temporary breaks */}
          {selectedReason && selectedConfig?.isTemporary && (
            <div className="mt-6 p-4 bg-surface-2 border border-hairline rounded-lg">
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Tiempo estimado de regreso
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={5}
                  max={120}
                  step={5}
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(Number(e.target.value))}
                  className="flex-1 h-2 bg-surface-3 rounded-lg appearance-none cursor-pointer accent-accent"
                />
                <div className="flex items-center gap-2 px-3 py-2 bg-surface rounded-lg border border-hairline">
                  <Clock size={16} className="text-ink-subtle" />
                  <span className="font-semibold text-ink tabular-nums min-w-[4rem] text-center">
                    {durationMinutes} min
                  </span>
                </div>
              </div>
              <p className="mt-2 text-sm text-ink-subtle tabular-nums">
                Regreso aproximado: {new Date(Date.now() + durationMinutes * 60 * 1000).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          )}

          {/* Action button */}
          {(currentStatus === 'available' || selectedReason !== currentReason) && selectedReason && (
            <button
              onClick={handleSetUnavailable}
              disabled={loading || !selectedReason}
              className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-4 bg-accent text-on-accent hover:bg-accent-hover disabled:opacity-50 font-bold rounded-xl transition-colors"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  {selectedConfig?.icon && <selectedConfig.icon size={20} />}
                  {selectedReason === 'day_off'
                    ? 'Marcar como no disponible hoy'
                    : `Pausar ${durationMinutes} minutos`
                  }
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
